import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { filter, map } from 'rxjs/operators';

export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  
  constructor(
    private meta: Meta,
    private title: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Only initialize route listener in browser, not during SSR prerendering
    if (isPlatformBrowser(this.platformId)) {
      this.initRouteDataListener();
    }
  }

  private initRouteDataListener() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .subscribe(route => {
        const routeData = route.snapshot.data;
        const currentUrl = this.router.url;

        if (routeData) {
          this.updateSEOData({
            title: routeData['title'],
            description: routeData['description'],
            canonical: routeData['canonical'] || `https://chatbot.pezetasolutions.com${currentUrl}`,
            robots: routeData['robots']
          });
        } else {
          // Fallback for routes without data
          this.updateSEOData({
            canonical: `https://chatbot.pezetasolutions.com${currentUrl}`
          });
        }
      });
  }

  /**
   * Apply SEO from route data. This method works in both SSR and browser.
   * Call this in component ngOnInit() to ensure SEO is applied during prerendering.
   *
   * @param routeData - The route data from ActivatedRoute.snapshot.data
   * @param currentUrl - The current URL path (from Router.url or manual path)
   */
  updateSEOFromRoute(routeData: any, currentUrl: string) {
    if (routeData) {
      this.updateSEOData({
        title: routeData['title'],
        description: routeData['description'],
        canonical: routeData['canonical'] || `https://chatbot.pezetasolutions.com${currentUrl}`,
        robots: routeData['robots'],
        keywords: routeData['keywords'],
        ogTitle: routeData['ogTitle'] || routeData['title'],
        ogDescription: routeData['ogDescription'] || routeData['description'],
        ogUrl: routeData['canonical'] || `https://chatbot.pezetasolutions.com${currentUrl}`,
        ogImage: routeData['ogImage']
      });
    } else {
      // Fallback for routes without data
      this.updateSEOData({
        canonical: `https://chatbot.pezetasolutions.com${currentUrl}`
      });
    }
  }

  updateSEOData(seoData: SEOData) {
    // Update title
    if (seoData.title) {
      this.title.setTitle(seoData.title);
    }

    // Update meta description
    if (seoData.description) {
      this.meta.updateTag({ name: 'description', content: seoData.description });
    }

    // Update keywords
    if (seoData.keywords) {
      this.meta.updateTag({ name: 'keywords', content: seoData.keywords });
    }

    // Update robots directive
    if (seoData.robots) {
      this.meta.updateTag({ name: 'robots', content: seoData.robots });
    }

    // Update canonical URL
    if (seoData.canonical) {
      this.updateCanonical(seoData.canonical);
    }

    // Update Open Graph tags
    if (seoData.ogTitle) {
      this.meta.updateTag({ property: 'og:title', content: seoData.ogTitle });
    }
    if (seoData.ogDescription) {
      this.meta.updateTag({ property: 'og:description', content: seoData.ogDescription });
    }
    if (seoData.ogImage) {
      this.meta.updateTag({ property: 'og:image', content: seoData.ogImage });
    }
    if (seoData.ogUrl) {
      this.meta.updateTag({ property: 'og:url', content: seoData.ogUrl });
    }

    // Update Twitter Card tags
    if (seoData.twitterTitle) {
      this.meta.updateTag({ name: 'twitter:title', content: seoData.twitterTitle });
    }
    if (seoData.twitterDescription) {
      this.meta.updateTag({ name: 'twitter:description', content: seoData.twitterDescription });
    }
    if (seoData.twitterImage) {
      this.meta.updateTag({ name: 'twitter:image', content: seoData.twitterImage });
    }

    // Add structured data
    if (seoData.structuredData) {
      this.addStructuredData(seoData.structuredData);
    }
  }

  private updateCanonical(url: string) {
    // Ensure URL is absolute and uses www
    const canonicalUrl = this.normalizeCanonicalUrl(url);
    
    const existingCanonical = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (existingCanonical) {
      existingCanonical.setAttribute('href', canonicalUrl);
    } else {
      const canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(canonicalLink);
    }
  }

  private normalizeCanonicalUrl(url: string): string {
    // If URL is relative, make it absolute
    if (url.startsWith('/')) {
      return `https://chatbot.pezetasolutions.com${url}`;
    }
    
    // If URL uses the apex domain or www subdomain, normalize to the chatbot subdomain
    if (url.startsWith('https://pezetasolutions.com')) {
      return url.replace('https://pezetasolutions.com', 'https://chatbot.pezetasolutions.com');
    }
    if (url.startsWith('https://www.pezetasolutions.com')) {
      return url.replace('https://www.pezetasolutions.com', 'https://chatbot.pezetasolutions.com');
    }

    // If URL is already on the chatbot subdomain, return as is
    if (url.startsWith('https://chatbot.pezetasolutions.com')) {
      return url;
    }
    
    // Default fallback
    return `https://chatbot.pezetasolutions.com${url.startsWith('/') ? url : '/' + url}`;
  }

  private addStructuredData(data: any) {
    // Remove existing structured data
    const existingScript = this.document.querySelector('script[type="application/ld+json"][data-dynamic]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic', 'true');
    script.textContent = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  // Method to generate structured data for services
  generateServiceStructuredData(serviceName: string, description: string) {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": serviceName,
      "description": description,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Pezeta Solutions",
        "url": "https://chatbot.pezetasolutions.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Los Mochis",
          "addressRegion": "Sinaloa",
          "addressCountry": "MX"
        }
      },
      "areaServed": ["Los Mochis", "Sinaloa", "México"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": serviceName,
        "itemListElement": [{
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": serviceName,
            "description": description
          }
        }]
      }
    };
  }

  // Method to generate breadcrumb structured data
  generateBreadcrumbStructuredData(breadcrumbs: Array<{name: string, url: string}>) {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  }
}